/* ==========================================================
   CPB SYMBOL FONT — SHOPIFY CART
   ----------------------------------------------------------
   1. Restores checkout-safe symbols back to PUA in cart:
      💕 → U+E000
      ❤  → U+E001
      ♥  → U+E002

   2. Wraps U+E000 through U+F8FF with .cpb-cart-symbol.

   3. Handles normal page load, AJAX cart updates,
      text-node updates, and back/forward navigation.
   ========================================================== */

(function () {
  'use strict';

  const SOURCE_PROPERTY = ' - Initials';

  const REVERSE_SYMBOL_MAP = {
    '💕': '\uE000',
    '\u2764': '\uE001',
    '\u2665': '\uE002'
  };

  const PUA_REGEX = /[\uE000-\uF8FF]/g;


  /* ==========================================================
     RESTORE CHECKOUT-SAFE SYMBOLS TO PUA
     ========================================================== */

  function restorePUAValue(value) {
    return Array.from(value || '')
      .map(function (character) {
        return REVERSE_SYMBOL_MAP[character] || character;
      })
      .join('');
  }


  function containsCheckoutSymbol(value) {
    return Array.from(value || '')
      .some(function (character) {
        return Object.prototype.hasOwnProperty.call(
          REVERSE_SYMBOL_MAP,
          character
        );
      });
  }


  async function restoreCartInitials() {
    try {
      const cartResponse = await fetch(
        window.Shopify.routes.root + 'cart.js',
        {
          credentials: 'same-origin'
        }
      );

      if (!cartResponse.ok) {
        throw new Error('Unable to read Shopify cart.');
      }

      const cart = await cartResponse.json();

      let cartChanged = false;


      for (const item of cart.items) {
        const properties = item.properties || {};

        const currentInitials =
          properties[SOURCE_PROPERTY];

        if (!currentInitials) {
          continue;
        }

        if (!containsCheckoutSymbol(currentInitials)) {
          continue;
        }

        const restoredInitials =
          restorePUAValue(currentInitials);

        if (restoredInitials === currentInitials) {
          continue;
        }

        const updatedProperties = {
          ...properties,
          [SOURCE_PROPERTY]: restoredInitials
        };


        /*
          Remove old duplicate property if present.
        */
        if (
          Object.prototype.hasOwnProperty.call(
            updatedProperties,
            'Initials'
          )
        ) {
          delete updatedProperties.Initials;
        }


        const updateResponse = await fetch(
          window.Shopify.routes.root + 'cart/change.js',
          {
            method: 'POST',

            credentials: 'same-origin',

            headers: {
              'Content-Type': 'application/json'
            },

            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity,
              properties: updatedProperties
            })
          }
        );

        if (!updateResponse.ok) {
          throw new Error(
            'Unable to restore CPB initials in cart.'
          );
        }

        cartChanged = true;
      }


      return cartChanged;

    } catch (error) {
      console.error(
        'CPB cart initials restore:',
        error
      );

      return false;
    }
  }


  /* ==========================================================
     PROCESS ONE TEXT NODE
     ========================================================== */

  function processTextNode(textNode) {
    const text = textNode.nodeValue;

    if (!text) {
      return;
    }

    PUA_REGEX.lastIndex = 0;

    if (!PUA_REGEX.test(text)) {
      PUA_REGEX.lastIndex = 0;
      return;
    }

    PUA_REGEX.lastIndex = 0;

    const parent = textNode.parentElement;

    if (!parent) {
      return;
    }

    if (
      parent.closest(
        'script, style, textarea, input, select, option, .cpb-cart-symbol'
      )
    ) {
      return;
    }

    const fragment = document.createDocumentFragment();

    let lastIndex = 0;
    let match;

    while ((match = PUA_REGEX.exec(text)) !== null) {

      if (match.index > lastIndex) {
        fragment.appendChild(
          document.createTextNode(
            text.slice(lastIndex, match.index)
          )
        );
      }

      const symbolSpan = document.createElement('span');

      symbolSpan.className = 'cpb-cart-symbol';
      symbolSpan.textContent = match[0];

      fragment.appendChild(symbolSpan);

      lastIndex = PUA_REGEX.lastIndex;
    }


    if (lastIndex < text.length) {
      fragment.appendChild(
        document.createTextNode(
          text.slice(lastIndex)
        )
      );
    }

    textNode.parentNode.replaceChild(
      fragment,
      textNode
    );
  }


  /* ==========================================================
     PROCESS AN ELEMENT
     ========================================================== */

  function processElement(root) {
    if (!root) {
      return;
    }

    if (root.nodeType === Node.TEXT_NODE) {
      processTextNode(root);
      return;
    }

    if (root.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    if (
      root.matches &&
      root.matches('.cpb-cart-symbol')
    ) {
      return;
    }

    const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT
    );

    const textNodes = [];

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach(processTextNode);
  }


  function processCurrentCart() {
    if (document.body) {
      processElement(document.body);
    }
  }


  /* ==========================================================
     INITIALISE
     ========================================================== */

  async function initCartSymbols() {

    /*
      First restore checkout-safe symbols back to PUA.
    */
    const cartChanged =
      await restoreCartInitials();


    /*
      If Shopify cart data changed, reload so the theme
      renders the restored line-item property cleanly.
    */
    if (cartChanged) {
      window.location.reload();
      return;
    }


    /*
      Otherwise process current rendered PUA symbols.
    */
    processCurrentCart();


    const observer = new MutationObserver(function (mutations) {

      mutations.forEach(function (mutation) {

        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function (node) {
            processElement(node);
          });
        }

        if (mutation.type === 'characterData') {
          processTextNode(mutation.target);
        }

      });

    });


    observer.observe(
      document.body,
      {
        childList: true,
        characterData: true,
        subtree: true
      }
    );
  }


  /* ==========================================================
     START
     ========================================================== */

  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      initCartSymbols,
      { once: true }
    );

  } else {

    initCartSymbols();

  }


  /* ==========================================================
     BACK / FORWARD CACHE
     ========================================================== */

  window.addEventListener(
    'pageshow',

    function (event) {

      /*
        If browser restores this page from history,
        run the restore check again.
      */
      if (event.persisted) {
        restoreCartInitials()
          .then(function (cartChanged) {

            if (cartChanged) {
              window.location.reload();
              return;
            }

            processCurrentCart();

          });
      }

    }
  );

})();